/**
 * TipTap extension that opens the placeholder suggestion popup when the user types "_".
 *
 * Unlike the mention extensions this doesn't introduce a node type: selecting a suggestion
 * replaces the trigger text with the same content HtmlEditor's insertPlaceholder() produces
 * (the resolved value, or a placeholderToken chip when unresolved).
 */
import { Extension } from "@tiptap/core";
import { PluginKey } from "@tiptap/pm/state";
import Suggestion from "@tiptap/suggestion";
import { getPlaceholderSuggestionItems, PlaceholderSuggestionItem } from "./placeholder-suggestion-items";
import { createSuggestionRenderer } from "./suggestion-renderer";
import PlaceholderSuggestionList from "./PlaceholderSuggestionList.vue";

export interface PlaceholderSuggestionOptions {
  resolveToken: (token: string) => unknown;
}

export const PlaceholderSuggestion = Extension.create<PlaceholderSuggestionOptions>({
  name: "placeholderSuggestion",

  addOptions() {
    return {
      resolveToken: () => undefined
    };
  },

  addProseMirrorPlugins() {
    const resolveToken = this.options.resolveToken;

    return [
      Suggestion<PlaceholderSuggestionItem>({
        editor: this.editor,
        char: "_",
        pluginKey: new PluginKey("placeholderSuggestion"),
        items: ({ query }) => getPlaceholderSuggestionItems(query, resolveToken),
        render: createSuggestionRenderer<PlaceholderSuggestionItem>(PlaceholderSuggestionList),
        command: ({ editor, range, props }) => {
          const resolved = resolveToken(props.token);
          const isSimpleValue = typeof resolved === "string" || typeof resolved === "number";
          const content =
            isSimpleValue && resolved !== ""
              ? String(resolved)
              : { type: "placeholderToken", attrs: { token: props.token } };

          editor.chain().focus().insertContentAt(range, content).run();
        }
      })
    ];
  }
});
